import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Button, Stack, Alert,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";

import { SECTION_COLORS } from "../../constants";
import {
    resolveConditionsFormat,
    usesLoyaltyConditionBuilder,
    CONDITIONS_FORMAT_UNOMI_ONLY,
} from "./segmentConditionsUtils";

const JsonBlock = ({ label, data }) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return null;
    const isEmpty = typeof data === "object" && !Array.isArray(data) && Object.keys(data).length === 0;
    if (isEmpty) return null;
    return (
        <Box mt={1.5} p={1.5} sx={{ borderRadius: 2, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
            <Typography fontSize={11} fontWeight={700} color={SECTION_COLORS.segments.main} mb={0.5}>{label}</Typography>
            <Typography component="pre" fontSize={11}
                sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#1a1a2e", m: 0 }}>
                {JSON.stringify(data, null, 2)}
            </Typography>
        </Box>
    );
};

const ViewSegmentDialog = ({
    open,
    segment,
    onClose,
    onEditRequest,
    onViewMembers,
}) => {
    if (!segment) return null;

    const conditionsFormat = resolveConditionsFormat(segment);
    const loyaltyAst = usesLoyaltyConditionBuilder(segment);

    const rows = [
        { label: "Nom", value: segment.name || "–" },
        { label: "Description", value: segment.description || "–" },
        { label: "Origine", value: segment.provider === "UNOMI" ? "CDP Unomi" : "Moteur fidélité" },
        { label: "Type", value: segment.is_dynamic ? "Dynamique" : "Statique" },
        { label: "Format conditions", value: conditionsFormat },
        { label: "Marque", value: segment.brand },
        { label: "Membres (moteur)", value: typeof segment.member_count === "number" ? segment.member_count : "–" },
        { label: "Suppression autorisée", value: segment.can_delete === false ? "Non" : "Oui" },
        { label: "Dernier calcul", value: segment.last_computed_at ? new Date(segment.last_computed_at).toLocaleString("fr-FR") : "–" },
        { label: "Créé le", value: segment.created_at ? new Date(segment.created_at).toLocaleDateString("fr-FR") : "–" },
        { label: "ID moteur (règles / jobs)", value: segment.id },
    ];

    if (segment.unomi_segment_id) {
        rows.push({
            label: "ID Unomi (CDP)",
            value: segment.unomi_segment_id,
        });
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.segments.gradient }}>
                        <GroupWorkIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                            {segment.name}
                        </Typography>
                        <Typography fontSize={11} color="text.secondary">Détail du segment</Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    Dans les règles et jobs internes, utilisez toujours l’<strong>UUID moteur</strong> ci-dessous,
                    jamais <code>unomi_segment_id</code>.
                </Alert>

                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={2} py={1.25}
                    sx={{
                        borderRadius: 2,
                        bgcolor: segment.active ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${segment.active ? "#86efac" : "#fca5a5"}`,
                    }}>
                    {segment.active
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                        : <CancelIcon sx={{ fontSize: 18, color: "#DC2626" }} />}
                    <Typography fontSize={13} fontWeight={600} color={segment.active ? "#16a34a" : "#DC2626"}>
                        {segment.active ? "Actif" : "Inactif"}
                    </Typography>
                    <Chip label={segment.is_dynamic ? "Dynamique" : "Statique"} size="small"
                        sx={{
                            ml: "auto", fontWeight: 700, fontSize: 11,
                            bgcolor: SECTION_COLORS.segments.light, color: SECTION_COLORS.segments.main,
                        }} />
                </Box>

                {segment.is_dynamic && segment.needs_recompute && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        Recalcul requis — les membres affichés peuvent être obsolètes tant que le moteur n’a pas recalculé.
                    </Alert>
                )}

                <Stack spacing={1} mb={1}>
                    {rows.map(({ label, value }) => (
                        <Box key={label} display="flex" alignItems="flex-start" justifyContent="space-between"
                            px={2} py={1} sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={12} color="text.secondary" fontWeight={500} sx={{ minWidth: 150 }}>{label}</Typography>
                            <Typography fontSize={12} fontWeight={600} color="#1a1a2e" sx={{ textAlign: "right", wordBreak: "break-all" }}>{value}</Typography>
                        </Box>
                    ))}
                </Stack>

                {segment.is_dynamic && (
                    <>
                        {loyaltyAst && <JsonBlock label="conditions (loyalty AST)" data={segment.conditions} />}
                        {!loyaltyAst && (
                            <>
                                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                                    Condition CDP non traduisible en builder loyalty ({CONDITIONS_FORMAT_UNOMI_ONLY}).
                                </Alert>
                                <JsonBlock label="unomi_condition" data={segment.unomi_condition} />
                            </>
                        )}
                        {loyaltyAst && segment.unomi_condition && (
                            <JsonBlock label="unomi_condition (sync CDP)" data={segment.unomi_condition} />
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1, flexWrap: "wrap" }}>
                <Button onClick={onClose} sx={{ color: "text.secondary", textTransform: "none" }}>Fermer</Button>
                {onViewMembers && (
                    <Button
                        variant="outlined"
                        onClick={() => { onClose(); onViewMembers(segment); }}
                        startIcon={<PeopleAltIcon />}
                        sx={{
                            borderColor: SECTION_COLORS.segments.main,
                            color: SECTION_COLORS.segments.main,
                            textTransform: "none", borderRadius: 2, fontWeight: 600, px: 2,
                            "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}10` },
                        }}
                    >
                        {segment.is_dynamic ? "Voir les membres" : "Gérer les membres"}
                    </Button>
                )}
                <Button variant="contained" onClick={() => { onClose(); onEditRequest(segment); }}
                    startIcon={<EditIcon />}
                    sx={{ background: SECTION_COLORS.segments.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    Modifier
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewSegmentDialog;
